import { Fragment, type DragEvent, type ReactNode } from "react";
import { NavLink } from "react-router-dom";

import type { NavigationNode } from "../../api/types";
import { cx } from "../../lib/cx";
import { Menu, type MenuItem } from "../../ui/Menu";
import { sameTarget, type SidebarDrag, type SidebarDropTarget } from "./dnd";

export interface TreeHandlers {
  drag: SidebarDrag | null;
  dropTarget: SidebarDropTarget | null;
  onDragStart: (drag: SidebarDrag) => void;
  onDragEnd: () => void;
  onDragOverTarget: (target: SidebarDropTarget) => void;
  onDrop: (target: SidebarDropTarget) => void;
}

export interface DocumentTreeProps extends TreeHandlers {
  collectionId: string;
  nodes: readonly NavigationNode[];
  parentDocumentId: string | null;
  /** Names the container in the drop zones' accessible labels. */
  containerLabel: string;
  depth: number;
  expanded: ReadonlySet<string>;
  onToggle: (id: string) => void;
  rowActions: (node: NavigationNode, parentDocumentId: string | null) => MenuItem[];
}

export function nodeLabel(node: NavigationNode): string {
  return node.title === "" ? "Untitled" : node.title;
}

function DropZone({
  target,
  name,
  active,
  onDragOverTarget,
  onDrop,
}: {
  target: SidebarDropTarget;
  name: string;
  active: boolean;
  onDragOverTarget: (target: SidebarDropTarget) => void;
  onDrop: (target: SidebarDropTarget) => void;
}): ReactNode {
  const over = (event: DragEvent): void => {
    event.preventDefault();
    onDragOverTarget(target);
  };
  return (
    <li
      role="separator"
      aria-label={name}
      onDragOver={over}
      onDragEnter={over}
      onDrop={(event) => {
        event.preventDefault();
        onDrop(target);
      }}
      className={cx("-my-px h-1 rounded", active ? "bg-accent" : "bg-transparent")}
    />
  );
}

/**
 * A collection's document tree. The whole structure arrives in one
 * `collections.documents` call when the collection is first expanded, so
 * opening a document node afterwards costs nothing; the lazy unit is the
 * collection, which is where the weight is.
 */
export function DocumentTree(props: DocumentTreeProps): ReactNode {
  const {
    collectionId,
    nodes,
    parentDocumentId,
    containerLabel,
    depth,
    expanded,
    onToggle,
    rowActions,
    drag,
    dropTarget,
    onDragStart,
    onDragEnd,
    onDragOverTarget,
    onDrop,
  } = props;

  const zone = (position: number, name: string): ReactNode => {
    const target: SidebarDropTarget = { type: "between", collectionId, parentDocumentId, position };
    return (
      <DropZone
        target={target}
        name={name}
        active={drag !== null && sameTarget(dropTarget, target)}
        onDragOverTarget={onDragOverTarget}
        onDrop={onDrop}
      />
    );
  };

  return (
    <ul className="flex flex-col">
      {nodes.map((node, position) => {
        const isOpen = expanded.has(node.id);
        const inside: SidebarDropTarget = { type: "inside", collectionId, documentId: node.id };
        const actions = rowActions(node, parentDocumentId);
        return (
          <Fragment key={node.id}>
            {zone(position, `Drop before ${nodeLabel(node)}`)}
            <li
              draggable
              onDragStart={() => {
                onDragStart({
                  documentId: node.id,
                  title: nodeLabel(node),
                  collectionId,
                  parentDocumentId,
                  index: position,
                });
              }}
              onDragEnd={onDragEnd}
              onDragOver={(event) => {
                event.preventDefault();
                onDragOverTarget(inside);
              }}
              onDrop={(event) => {
                event.preventDefault();
                onDrop(inside);
              }}
              className={cx(
                "flex items-center gap-1 rounded-md",
                drag !== null && sameTarget(dropTarget, inside) && "ring-1 ring-accent",
              )}
              style={{ paddingLeft: `${String(depth * 12)}px` }}
            >
              <button
                type="button"
                aria-label={`${isOpen ? "Collapse" : "Expand"} ${nodeLabel(node)}`}
                aria-expanded={isOpen}
                disabled={node.children.length === 0}
                onClick={() => {
                  onToggle(node.id);
                }}
                className="w-4 shrink-0 text-xs text-muted disabled:opacity-0"
              >
                {isOpen ? "▾" : "▸"}
              </button>
              <NavLink
                to={node.url}
                className={({ isActive }) =>
                  cx(
                    "flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-sm",
                    isActive
                      ? "bg-hover font-medium text-ink"
                      : "text-muted hover:bg-hover hover:text-ink",
                  )
                }
              >
                <span aria-hidden="true" className="w-4 shrink-0 text-center">
                  {node.icon ?? "📄"}
                </span>
                <span className="truncate">{nodeLabel(node)}</span>
              </NavLink>
              {actions.length > 0 && (
                <Menu
                  buttonLabel={`Actions for ${nodeLabel(node)}`}
                  menuLabel="Document actions"
                  items={actions}
                  trigger="⋯"
                />
              )}
            </li>
            {isOpen && node.children.length > 0 && (
              <li>
                <DocumentTree
                  {...props}
                  nodes={node.children}
                  parentDocumentId={node.id}
                  containerLabel={nodeLabel(node)}
                  depth={depth + 1}
                />
              </li>
            )}
          </Fragment>
        );
      })}
      {zone(
        nodes.length,
        nodes.length === 0 ? `Drop into ${containerLabel}` : `Drop at the end of ${containerLabel}`,
      )}
    </ul>
  );
}
